import { FormEvent, useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  connectCustomerApp,
  getCustomerApps,
  getCustomerJobEncryption,
  type CustomerAppsState,
  type CustomerDeploymentStatus,
} from './api.js';
import { encryptCustomerJobPayload } from './customer-job-crypto.js';
import {
  curatedApps,
  type CuratedAppDefinition,
} from '../curated-apps.js';

export function CustomerAppsEditor({
  installation,
  onDirtyChange,
}: {
  installation: CustomerDeploymentStatus;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [state, setState] = useState<CustomerAppsState | null>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<CuratedAppDefinition | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void getCustomerApps(installation.installationId)
      .then((value) => { if (active) setState(value); })
      .catch((caught: unknown) => { if (active) setError(message(caught)); });
    return () => { active = false; };
  }, [installation.installationId]);

  const visible = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return curatedApps;
    return curatedApps.filter((app) =>
      `${app.name} ${app.domain} ${app.tool}`.toLowerCase().includes(normalized));
  }, [query]);
  const connected = new Set(state?.connections.map((item) => item.providerId) ?? []);

  return (
    <section className="curated-apps" aria-label="Curated app access">
      <div className="curated-apps-toolbar">
        <div>
          <span className="eyebrow">Credential-brokered apps</span>
          <h3>Choose apps your secure desktops can reach</h3>
          <p>Credentials are encrypted for your AWS KMS key and stored only in your account.</p>
        </div>
        <label>
          <span className="sr-only">Search apps</span>
          <input
            type="search"
            placeholder="Search 46 apps"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      </div>
      {error && <p className="curated-app-error" role="alert">{error}</p>}
      {!state && !error ? <p className="curated-app-loading">Loading app access…</p> : (
        <div className="curated-app-grid">
          {visible.map((app) => (
            <button
              type="button"
              className="curated-app-card"
              key={app.id}
              onClick={() => setSelected(app)}
              style={{ '--app-accent': app.accent } as CSSProperties}
            >
              <span className="curated-app-mark" aria-hidden="true">{initials(app.name)}</span>
              <span>
                <strong>{app.name}</strong>
                <small>{app.domain}</small>
              </span>
              <i>{connected.has(app.id) ? 'Connected' : 'Connect'}</i>
              <em>{app.tool.toUpperCase()}</em>
            </button>
          ))}
        </div>
      )}
      {selected && state && (
        <ConnectAppDialog
          app={selected}
          installation={installation}
          revision={state.revision}
          connected={connected.has(selected.id)}
          onDirtyChange={onDirtyChange}
          onClose={() => {
            onDirtyChange(false);
            setSelected(null);
          }}
          onConnected={(connection, revision) => {
            setState((current) => ({
              revision,
              connections: [
                ...(current?.connections ?? []).filter((item) => item.providerId !== connection.providerId),
                connection,
              ],
            }));
            onDirtyChange(false);
            setSelected(null);
          }}
        />
      )}
    </section>
  );
}

function ConnectAppDialog({
  app,
  installation,
  revision,
  connected,
  onDirtyChange,
  onClose,
  onConnected,
}: {
  app: CuratedAppDefinition;
  installation: CustomerDeploymentStatus;
  revision: number;
  connected: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onClose: () => void;
  onConnected: (
    connection: Awaited<ReturnType<typeof connectCustomerApp>>['connection'],
    revision: number,
  ) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<'recommended' | 'advanced'>('recommended');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const configurableHosts = app.routes.filter((route) => route.configurable && route.hostField);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const missing = app.fields.find((field) => field.required !== false && !values[field.key]?.trim());
      if (missing) throw new Error(`${cleanLabel(missing.label)} is required.`);
      const configuration = Object.fromEntries(configurableHosts.map((route) => [
        route.hostField!,
        values[route.hostField!] ?? '',
      ]));
      const encryption = await getCustomerJobEncryption(installation.installationId);
      const encrypted = await encryptCustomerJobPayload(encryption, {
        providerId: app.id,
        credentials: Object.fromEntries(app.fields
          .filter((field) => values[field.key]?.trim())
          .map((field) => [field.key, values[field.key]!.trim()])),
      });
      encrypted.contentKey.fill(0);
      const result = await connectCustomerApp({
        installationId: installation.installationId,
        providerId: app.id,
        expectedRevision: revision,
        configuration,
        encryptedCredential: encrypted.envelope,
      });
      onConnected(result.connection, result.revision);
    } catch (caught) {
      setError(message(caught));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="curated-connect-scrim" role="presentation">
      <form className="curated-connect-dialog" onSubmit={submit} aria-label={`Connect ${app.name}`}>
        <header>
          <span className="curated-app-mark" style={{ '--app-accent': app.accent } as CSSProperties}>{initials(app.name)}</span>
          <div><span className="eyebrow">{connected ? 'Update connection' : 'Connect app'}</span><h3>{app.name}</h3></div>
          <button type="button" onClick={onClose} aria-label={`Close ${app.name}`}>×</button>
        </header>
        <nav aria-label="Connection settings">
          <button type="button" aria-pressed={tab === 'recommended'} onClick={() => setTab('recommended')}>Recommended</button>
          <button type="button" aria-pressed={tab === 'advanced'} onClick={() => setTab('advanced')}>Advanced</button>
        </nav>
        {tab === 'recommended' ? (
          <div className="curated-connect-fields">
            <p>Enter the credential in your browser. Cyrus receives only an encrypted envelope.</p>
            {app.fields.map((field) => (
              <label key={field.key}>
                <span>{cleanLabel(field.label)}{field.required === false ? ' (optional)' : ''}</span>
                <input
                  type={field.secret ? 'password' : 'text'}
                  autoComplete="off"
                  value={values[field.key] ?? ''}
                  placeholder={field.placeholder}
                  onChange={(event) => {
                    setValues((current) => ({ ...current, [field.key]: event.target.value }));
                    onDirtyChange(true);
                  }}
                />
              </label>
            ))}
            {app.scopes?.length ? <details><summary>OAuth scopes</summary><code>{app.scopes.join('\n')}</code></details> : null}
          </div>
        ) : (
          <div className="curated-connect-advanced">
            <dl>
              <div><dt>Credential type</dt><dd>{credentialLabel(app.credentialType)}</dd></div>
              <div><dt>Allowed websites</dt><dd>{app.routes.map((route) => route.host).join(', ')}</dd></div>
              <div><dt>Path prefixes</dt><dd>{app.routes.flatMap((route) => route.pathPrefixes ?? []).join(', ') || 'All paths'}</dd></div>
              <div><dt>Injected headers</dt><dd>{app.headers?.map((header) => header.name).join(', ') ?? 'Authorization'}</dd></div>
              <div><dt>Preferred runtime</dt><dd>{app.tool.toUpperCase()}</dd></div>
              <div><dt>Brokered CLI</dt><dd><code>cyrus-app request {app.id} &lt;METHOD&gt; &lt;PATH&gt;</code></dd></div>
            </dl>
          </div>
        )}
        {error && <p className="curated-app-error" role="alert">{error}</p>}
        <footer>
          <span>Stored in customer-owned Secrets Manager</span>
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={saving}>{saving ? 'Connecting…' : connected ? 'Update' : 'Connect'}</button>
        </footer>
      </form>
    </div>
  );
}

function initials(name: string): string {
  return name.replace(/\([^)]*\)/gu, '').split(/[.\s-]+/u).filter(Boolean).slice(0, 2)
    .map((part) => part[0]!.toUpperCase()).join('');
}

function cleanLabel(value: string): string {
  return value.replace(/^Claude's /u, '');
}

function credentialLabel(value: CuratedAppDefinition['credentialType']): string {
  return ({
    headers: 'Custom headers',
    basic: 'HTTP Basic',
    oauth2_authorization_code: 'OAuth 2.0 authorization',
    oauth2_client_credentials: 'OAuth 2.0 client credentials',
  })[value];
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
